import { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import clientService from "../services/clientService";
import Cookies from "js-cookie";
import "../css/ClientManagement.css";

// Modal component using React Portal for proper layering
const ClientModal = ({ isOpen, onClose, title, children }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="cm-modal-overlay" onClick={onClose}>
            <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="cm-modal-header">
                    <h3>{title}</h3>
                    <button className="cm-modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="cm-modal-body">
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
};

// Toast notification component
const Toast = ({ message, type, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, 4000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return ReactDOM.createPortal(
        <div className={`cm-toast ${type}`}>
            <span className="cm-toast-icon">
                {type === 'success' ? '✓' : '✕'}
            </span>
            {message}
        </div>,
        document.body
    );
};

// Client form component (reusable for create and edit)
const ClientForm = ({
    name, setName,
    forwardEmail, setForwardEmail,
    appfolioUrl, setAppfolioUrl,
    onSubmit, onCancel,
    submitLabel
}) => (
    <form onSubmit={onSubmit}>
        <div className="cm-form-group">
            <label className="cm-form-label">
                Client Name <span className="required">*</span>
            </label>
            <input
                type="text"
                className="cm-form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Enter client name"
            />
        </div>
        <div className="cm-form-group">
            <label className="cm-form-label">
                Smart Bill Entry E-mail
            </label>
            <input
                type="email"
                className="cm-form-input"
                value={forwardEmail}
                onChange={(e) => setForwardEmail(e.target.value)}
                placeholder="Optional - Enter forwarding email"
            />
        </div>
        <div className="cm-form-group">
            <label className="cm-form-label">
                AppFolio Website URL
            </label>
            <input
                type="url"
                className="cm-form-input"
                value={appfolioUrl}
                onChange={(e) => setAppfolioUrl(e.target.value)}
                placeholder="Optional - Enter AppFolio URL"
            />
        </div>
        <div className="cm-modal-actions">
            <button type="button" className="cm-btn secondary" onClick={onCancel}>
                Cancel
            </button>
            <button type="submit" className="cm-btn primary">
                {submitLabel}
            </button>
        </div>
    </form>
);

// Client card component
const ClientCard = ({ client, onEdit, onDelete, animationDelay }) => (
    <div
        className="cm-client-card"
        style={{ animationDelay: `${animationDelay}s` }}
    >
        <div className="cm-card-header">
            <h3 className="cm-client-name">{client.name}</h3>
            <div className="cm-card-actions">
                <button
                    className="cm-action-btn edit"
                    onClick={() => onEdit(client)}
                >
                    Edit
                </button>
                <button
                    className="cm-action-btn delete"
                    onClick={() => onDelete(client.id)}
                >
                    Delete
                </button>
            </div>
        </div>
        <div className="cm-card-body">
            <div className="cm-info-row">
                <span className="cm-info-label">Smart Bill Entry E-mail</span>
                <span className={`cm-info-value ${!client.forwardEmail ? 'empty' : ''}`}>
                    {client.forwardEmail || 'Not configured'}
                </span>
            </div>
            <div className="cm-info-row">
                <span className="cm-info-label">AppFolio Website URL</span>
                <span className={`cm-info-value ${!client.appfolioUrl ? 'empty' : ''}`}>
                    {client.appfolioUrl ? (
                        <a href={client.appfolioUrl} target="_blank" rel="noopener noreferrer">
                            {client.appfolioUrl}
                        </a>
                    ) : (
                        'Not configured'
                    )}
                </span>
            </div>
        </div>
    </div>
);

const ClientPage = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);

    // Create form state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createName, setCreateName] = useState("");
    const [createForwardEmail, setCreateForwardEmail] = useState("");
    const [createAppfolioUrl, setCreateAppfolioUrl] = useState("");

    // Edit form state
    const [editingClient, setEditingClient] = useState(null);
    const [editName, setEditName] = useState("");
    const [editForwardEmail, setEditForwardEmail] = useState("");
    const [editAppfolioUrl, setEditAppfolioUrl] = useState("");

    // Search and toast state
    const [clientSearch, setClientSearch] = useState("");
    const [toast, setToast] = useState(null);

    useEffect(() => {
        setIsAdmin(Cookies.get("isAdmin") === "true");
        fetchClients();
    }, []);

    const fetchClients = async () => {
        setLoading(true);
        try {
            const data = await clientService.getAllClients();
            setClients(data);
        } catch (error) {
            showToast(error.message || "Failed to fetch clients", "error");
        }
        setLoading(false);
    };

    const showToast = useCallback((message, type) => {
        setToast({ message, type });
    }, []);

    const closeToast = useCallback(() => {
        setToast(null);
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await clientService.createClient(createName, createForwardEmail, createAppfolioUrl);
            showToast("Client created successfully!", "success");
            setCreateName("");
            setCreateForwardEmail("");
            setCreateAppfolioUrl("");
            setShowCreateModal(false);
            fetchClients();
        } catch (error) {
            showToast(error.message || "Failed to create client", "error");
        }
    };

    const handleEdit = (client) => {
        setEditingClient(client);
        setEditName(client.name);
        setEditForwardEmail(client.forwardEmail || "");
        setEditAppfolioUrl(client.appfolioUrl || "");
    };

    const handleUpdateClient = async (e) => {
        e.preventDefault();
        try {
            await clientService.updateClient(editingClient.id, {
                name: editName,
                forwardEmail: editForwardEmail,
                appfolioUrl: editAppfolioUrl
            });
            showToast("Client updated successfully!", "success");
            setEditingClient(null);
            fetchClients();
        } catch (error) {
            showToast(error.message || "Failed to update client", "error");
        }
    };

    const cancelEdit = () => {
        setEditingClient(null);
        setEditName("");
        setEditForwardEmail("");
        setEditAppfolioUrl("");
    };

    const handleDelete = async (clientId) => {
        if (!window.confirm("Are you sure you want to delete this client? This action cannot be undone.")) {
            return;
        }
        try {
            await clientService.deleteClient(clientId);
            showToast("Client deleted successfully!", "success");
            fetchClients();
        } catch (error) {
            showToast(error.message || "Failed to delete client", "error");
        }
    };

    const filteredClients = clients.filter(client =>
        client.name.toLowerCase().includes(clientSearch.toLowerCase())
    );

    // Access denied view
    if (!isAdmin) {
        return (
            <div className="cm-access-denied">
                <div className="cm-access-denied-icon">🔒</div>
                <h2>Access Denied</h2>
                <p>You must be an administrator to manage clients.</p>
            </div>
        );
    }

    return (
        <div className="client-management">
            {/* Header */}
            <header className="cm-header">
                <div className="cm-header-content">
                    <div className="cm-title-section">
                        <h1 className="cm-title">Client Management</h1>
                    </div>
                    <button
                        className="cm-add-btn"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <span className="btn-icon">+</span>
                        Add Client
                    </button>
                </div>
            </header>

            {/* Stats Cards */}
            <div className="cm-stats">
                <div className="cm-stat-card">
                    <div className="cm-stat-icon total">👥</div>
                    <div className="cm-stat-info">
                        <h4>{clients.length}</h4>
                        <p>Total Clients</p>
                    </div>
                </div>
                <div className="cm-stat-card">
                    <div className="cm-stat-icon active">✉️</div>
                    <div className="cm-stat-info">
                        <h4>{clients.filter(c => c.forwardEmail).length}</h4>
                        <p>With Email Config</p>
                    </div>
                </div>
                <div className="cm-stat-card">
                    <div className="cm-stat-icon recent">🔗</div>
                    <div className="cm-stat-info">
                        <h4>{clients.filter(c => c.appfolioUrl).length}</h4>
                        <p>With AppFolio URL</p>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="cm-search-section">
                <div className="cm-search-wrapper">
                    <input
                        type="text"
                        className="cm-search-input"
                        placeholder="Search clients..."
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                    />
                    <span className="cm-search-icon">🔍</span>
                </div>
            </div>

            {/* Client Cards */}
            {loading ? (
                <div className="cm-loading">
                    <div className="cm-spinner"></div>
                    <span>Loading clients...</span>
                </div>
            ) : filteredClients.length === 0 ? (
                <div className="cm-empty">
                    <div className="cm-empty-icon">📋</div>
                    <h3>{clientSearch ? 'No matches found' : 'No clients yet'}</h3>
                    <p>{clientSearch ? 'Try a different search term' : 'Click "Add Client" to create your first client'}</p>
                </div>
            ) : (
                <div className="cm-clients-grid">
                    {filteredClients.map((client, index) => (
                        <ClientCard
                            key={client.id}
                            client={client}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            animationDelay={0.1 * (index % 6)}
                        />
                    ))}
                </div>
            )}

            {/* Create Modal */}
            <ClientModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Create New Client"
            >
                <ClientForm
                    name={createName}
                    setName={setCreateName}
                    forwardEmail={createForwardEmail}
                    setForwardEmail={setCreateForwardEmail}
                    appfolioUrl={createAppfolioUrl}
                    setAppfolioUrl={setCreateAppfolioUrl}
                    onSubmit={handleCreate}
                    onCancel={() => setShowCreateModal(false)}
                    submitLabel="Create Client"
                />
            </ClientModal>

            {/* Edit Modal */}
            <ClientModal
                isOpen={!!editingClient}
                onClose={cancelEdit}
                title="Edit Client"
            >
                <ClientForm
                    name={editName}
                    setName={setEditName}
                    forwardEmail={editForwardEmail}
                    setForwardEmail={setEditForwardEmail}
                    appfolioUrl={editAppfolioUrl}
                    setAppfolioUrl={setEditAppfolioUrl}
                    onSubmit={handleUpdateClient}
                    onCancel={cancelEdit}
                    submitLabel="Save Changes"
                />
            </ClientModal>

            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={closeToast}
                />
            )}
        </div>
    );
};

export default ClientPage;
